import { Badge } from '@/components/ui/badge';
import { EmptySkuIcon } from '../../icons';
import { PreviewTable } from '../../common/PreviewTable';
import { EmptyState } from '../../common/EmptyState';
import type { PreviewTableColumn } from '../../common/PreviewTable';
import type { Product, ProductInventoryItem, ProductContainerItem } from '../types';
import { useTranslation } from '@hooks/useTranslation';

interface EquipmentSectionProps {
  product: Product;
}

function ItemCell({ item }: { item: { name: string; category: string | null; image_url: string | null } }) {
  return (
    <div className="flex items-center gap-6">
      <div className="flex h-16 w-16 shrink-0 aspect-square items-center justify-center rounded-input bg-dinnerplate-white">
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} className="h-full w-full rounded-input object-cover" />
        ) : (
          <EmptySkuIcon className="text-grey" />
        )}
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-inter text-sm font-semibold text-off-black">{item.name}</span>
        {item.category && (
          <Badge
            variant="static"
            className="font-inter w-fit border-0 rounded-xs bg-dinnerplate-white text-grey text-xs font-semibold px-2 py-1 flex items-center justify-center leading-tight-sm"
          >
            {item.category}
          </Badge>
        )}
      </div>
    </div>
  );
}

export function EquipmentSection({ product }: EquipmentSectionProps) {
  const { t } = useTranslation();

  const inventoryColumns: PreviewTableColumn<ProductInventoryItem>[] = [
    {
      key: 'item',
      header: t('index.equipment.columns.item'),
      render: (item) => <ItemCell item={item} />,
      width: '1fr',
    },
    {
      key: 'quantity',
      header: t('index.equipment.columns.quantity'),
      render: (item) => (
        <span className="font-inter text-sm text-off-black">
          {item.quantity ?? '\u2014'}
        </span>
      ),
      width: '2fr',
    },
  ];

  const containerColumns: PreviewTableColumn<ProductContainerItem>[] = [
    {
      key: 'item',
      header: t('index.equipment.columns.item'),
      render: (item) => <ItemCell item={item} />,
      width: '3fr',
    },
    {
      key: 'capacity',
      header: t('index.equipment.columns.capacity'),
      render: (item) => (
        <span className="font-inter text-sm text-off-black">{item.capacity}</span>
      ),
      width: '3fr',
    },
    {
      key: 'minimum_pax_required',
      header: t('index.equipment.columns.minimum_guests_required'),
      render: (item) => (
        <span className="font-inter text-sm text-off-black">
          {item.minimum_pax_required ?? '\u2014'}
        </span>
      ),
      width: '3fr',
    },
  ];

  const emptyLabel = t('index.equipment.no_item');

  return (
    <div className="space-y-8">
      <h3 className="font-kapra-expanded text-2xl font-black uppercase leading-tight text-off-black">
        {t('index.equipment.title')}
      </h3>

      {/* Inventory set */}
      <div className="space-y-4">
        <h4 className="font-inter text-lg font-semibold text-off-black">
          {t('index.equipment.inventory_sets')}
        </h4>

        {product.inventory_items.length > 0 ? (
          <PreviewTable
            columns={inventoryColumns}
            data={product.inventory_items}
            getRowKey={(item) => item.id}
          />
        ) : (
          <EmptyState label={emptyLabel} />
        )}
      </div>

      {/* Container set */}
      <div className="space-y-4">
        <h4 className="font-inter text-lg font-semibold text-off-black">
          {t('index.equipment.container_sets')}
        </h4>

        {product.container_items.length > 0 ? (
          <PreviewTable
            columns={containerColumns}
            data={product.container_items}
            getRowKey={(item) => item.id}
          />
        ) : (
          <EmptyState label={emptyLabel} />
        )}
      </div>
    </div>
  );
}
